/**
 * Formatted console summary output.
 *
 * Prints a box-drawing formatted summary to stderr at test run completion.
 * Adapts to data present: e2e-only, API-only, or mixed.
 */

import type { Summary } from '@testrelic/core';

const BOX_WIDTH = 58;

function pad(text: string): string {
  const padding = BOX_WIDTH - 2 - text.length;
  return padding > 0 ? text + ' '.repeat(padding) : text;
}

function line(text: string): string {
  return `\u2502  ${pad(text)}  \u2502\n`;
}

/**
 * Print a formatted console summary to stderr.
 *
 * @param summary - Enriched summary statistics
 * @param outputPath - Path to the JSON output file
 * @param htmlReportPath - Path to the HTML report file
 * @param quiet - When true, suppresses all output
 */
export function printConsoleSummary(
  summary: Summary,
  outputPath: string,
  htmlReportPath: string,
  quiet: boolean,
): void {
  if (quiet) return;
  if (summary.total === 0) return;

  const top = `\u250C${'─'.repeat(BOX_WIDTH)}\u2510\n`;
  const bottom = `\u2514${'─'.repeat(BOX_WIDTH)}\u2518\n`;
  const blank = line('');

  let output = top;
  output += line('TestRelic AI - Playwright Test Report');
  output += blank;

  // Tests line
  const parts: string[] = [];
  if (summary.passed > 0) parts.push(`${summary.passed} \u2713`);
  if (summary.failed > 0) parts.push(`${summary.failed} \u2717`);
  if (summary.flaky > 0) parts.push(`${summary.flaky} \u26A0`);
  if (summary.skipped > 0) parts.push(`${summary.skipped} skipped`);
  if (summary.timedout > 0) parts.push(`${summary.timedout} timedout`);
  output += line(`Tests:       ${summary.total} total (${parts.join('  ')})`);

  // Navigation section (only if navigations present)
  if (summary.totalNavigations > 0) {
    output += line(
      `Navigations: ${summary.totalNavigations} visits across ${summary.uniqueNavigationUrls} unique URLs`,
    );
  }

  // API section (only if API calls present)
  if (summary.totalApiCalls > 0) {
    output += line(
      `API Calls:   ${summary.totalApiCalls} calls across ${summary.uniqueApiUrls} unique endpoints`,
    );

    // Method breakdown
    const methodParts = Object.entries(summary.apiCallsByMethod)
      .filter(([, count]) => count > 0)
      .map(([method, count]) => `${method}: ${count}`);
    if (methodParts.length > 0) {
      output += line(`  ${methodParts.join('  ')}`);
    }

    // Status breakdown (only non-zero)
    const statusParts = Object.entries(summary.apiCallsByStatusRange)
      .filter(([, count]) => count > 0)
      .map(([range, count]) => `${range}: ${count}`);
    if (statusParts.length > 0) {
      output += line(`  ${statusParts.join('  ')}`);
    }

    // Response times
    if (summary.apiResponseTime) {
      output += line(
        `  Avg response: ${summary.apiResponseTime.avg}ms  P95: ${summary.apiResponseTime.p95}ms`,
      );
    }
  }

  // Assertion section (only if assertions present)
  if (summary.totalAssertions > 0) {
    output += line(
      `Assertions:  ${summary.totalAssertions} total (${summary.passedAssertions} \u2713  ${summary.failedAssertions} \u2717)`,
    );
  }

  // Action steps section (only if action steps present)
  if (summary.totalActionSteps > 0) {
    const catParts: string[] = [];
    const catMap = summary.actionStepsByCategory;
    if (catMap['ui_action']) catParts.push(`${catMap['ui_action']} UI`);
    if (catMap['assertion']) catParts.push(`${catMap['assertion']} assertions`);
    if (catMap['custom_step']) catParts.push(`${catMap['custom_step']} custom`);
    const catStr = catParts.length > 0 ? ` (${catParts.join('  ')})` : '';
    output += line(`Actions:     ${summary.totalActionSteps} steps${catStr}`);
  }

  // Output paths
  output += line(`Report:      ${htmlReportPath}`);
  output += line(`Data:        ${outputPath}`);

  output += bottom;
  output += 'For more information visit us at https://docs.testrelic.ai\n';

  process.stderr.write(output);
}
